import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { api } from '../utils/api';
import { Plus, Truck, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    phone: '',
    cif_nif: '',
    address: ''
  });

  const fetchSuppliers = React.useCallback(async () => {
    try {
      const response = await api.getSuppliers();
      setSuppliers(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching suppliers:', error);
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // POLLING DESACTIVADO - causaba recargas visuales y pérdida de foco
  // useEffect(() => {
  //   const pollingInterval = setInterval(() => {
  //     fetchSuppliers();
  //   }, 5000);
  //   return () => clearInterval(pollingInterval);
  // }, [fetchSuppliers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await api.updateSupplier(editingSupplier.id, formData);
        toast.success('Proveedor actualizado');
      } else {
        await api.createSupplier(formData);
        toast.success('Proveedor creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
           ? error.response.data.detail 
           : 'Error de validación en los datos del proveedor')
        : 'Error al guardar proveedor';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact: supplier.contact || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      cif_nif: supplier.cif_nif || '',
      address: supplier.address || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este proveedor?')) return;
    try {
      await api.deleteSupplier(id);
      toast.success('Proveedor eliminado');
      fetchSuppliers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
           ? error.response.data.detail 
           : 'Error al eliminar proveedor')
        : 'Error al eliminar proveedor';
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact: '',
      email: '',
      phone: '',
      cif_nif: '',
      address: ''
    });
    setEditingSupplier(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">Proveedores</h1>
          <p className="text-base text-slate-600 dark:text-slate-300 mt-2">Gestiona tus proveedores</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button variant="primary" size="default" data-testid="add-supplier-button">
              <Plus className="h-5 w-5 mr-2" />
              Nuevo Proveedor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="supplier-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact">Persona de Contacto</Label>
                <Input
                  id="contact"
                  value={formData.contact}
                  onChange={(e) => setFormData({...formData, contact: e.target.value})}
                  data-testid="supplier-contact-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  data-testid="supplier-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  data-testid="supplier-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cif_nif">CIF / NIF</Label>
                <Input
                  id="cif_nif"
                  value={formData.cif_nif}
                  onChange={(e) => setFormData({...formData, cif_nif: e.target.value})}
                  placeholder="B12345678"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Dirección Completa</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Calle, Ciudad, CP"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button 
                  type="button" 
                  variant="secondary" 
                  size="default"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  variant="success"
                  size="default"
                  data-testid="save-supplier-button"
                >
                  {editingSupplier ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <CardHeader>
          <CardTitle>Lista de Proveedores</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase tracking-wider text-xs">Nombre</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Contacto</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Email</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Teléfono</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((supplier) => (
                  <TableRow key={supplier.id} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                    <TableCell className="font-medium">{supplier.name}</TableCell>
                    <TableCell>{supplier.contact || '-'}</TableCell>
                    <TableCell>{supplier.email || '-'}</TableCell>
                    <TableCell>{supplier.phone || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="secondary" 
                          size="icon-sm" 
                          onClick={() => handleEdit(supplier)}
                          data-testid={`edit-supplier-${supplier.id}`}
                          className="hover:bg-blue-100 hover:text-blue-700"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="danger" 
                          size="icon-sm" 
                          onClick={() => handleDelete(supplier.id)}
                          data-testid={`delete-supplier-${supplier.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {suppliers.map((supplier) => (
              <div key={supplier.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-lg text-slate-900 dark:text-white">{supplier.name}</h3>
                  <div className="flex gap-2">
                    <Button 
                      variant="secondary" 
                      size="icon-sm" 
                      onClick={() => handleEdit(supplier)}
                      className="hover:bg-blue-100 hover:text-blue-700"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="danger" 
                      size="icon-sm" 
                      onClick={() => handleDelete(supplier.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  {supplier.contact && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 dark:text-slate-400">Contacto:</span>
                      <span className="text-slate-900 dark:text-white">{supplier.contact}</span>
                    </div>
                  )}
                  {supplier.email && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 dark:text-slate-400">Email:</span>
                      <span className="text-slate-900 dark:text-white">{supplier.email}</span>
                    </div>
                  )}
                  {supplier.phone && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 dark:text-slate-400">Teléfono:</span>
                      <span className="text-slate-900 dark:text-white">{supplier.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {suppliers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No hay proveedores registrados
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
